import { useCallback } from 'react';
import {
  emptyPagedCollection,
  IPagingData,
  PayOrderDto,
  UpdatePayOrderDto
} from '@sr/dto';
import {
  fetchPayOrdersRelatedToInvoice,
  fetchPayOrderToInvoice,
  fetchPayOrderToPurchase
} from 'shared/api/payorders';
import { useReadOnlyRemoteCollection } from 'utils/remote-collection.hook';
import { IRemotePagedCollection } from 'utils/remote-paged-collection.hook';
import { payOrdersApi } from 'entities/pay-orders/pay-orders.api';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';

export const usePayOrders = (pagination: IPagingData, filter?: URLSearchParams) => {
  const { data, isLoading, error } = payOrdersApi.useGetPayOrdersQuery(
    { paging: pagination, 
      filter: filter?.toString() || ''
    },
    { refetchOnMountOrArgChange: true });

  const result: IRemotePagedCollection<PayOrderDto> = {
    collection: data || emptyPagedCollection,
    error: extractNestJsErrorMessage(error),
    isLoading: isLoading
  };

  return result;
};

export const useInvoiceRelatedPayOrders = (invoiceId: number) => {
  return useReadOnlyRemoteCollection<PayOrderDto>({
    fetchHandler: useCallback(
      () => fetchPayOrdersRelatedToInvoice(invoiceId),
      [invoiceId]
    )
  });
};

export const useInvoicePayOrders = (invoiceId: number) => {
  return useReadOnlyRemoteCollection<PayOrderDto>({
    fetchHandler: useCallback(
      () => fetchPayOrderToInvoice(invoiceId),
      [invoiceId]
    )
  });
};

export const usePurchasePayOrders = (invoiceId: number, purchaseId: number) => {
  return useReadOnlyRemoteCollection<PayOrderDto>({
    fetchHandler: useCallback(
      () => fetchPayOrderToPurchase(invoiceId, purchaseId),
      [invoiceId, purchaseId]
    )
  });
};

export const usePayOrder = (id: number) => {
  const { currentData, isFetching, error } = payOrdersApi.useGetPayOrderQuery(id, { refetchOnMountOrArgChange: true });
  const [updatePayOrder, { isLoading: isUpdating }] = payOrdersApi.useUpdatePayOrderMutation();
  const [deletePayOrder, { isLoading: isDeleting }] = payOrdersApi.useDeletePayOrderMutation();
  const item = currentData ? PayOrderDto.fromPlain(currentData) : null;

  return {
    item,
    updateItem: (data: UpdatePayOrderDto) => updatePayOrder(data).unwrap(),
    deleteItem: () => deletePayOrder(id).unwrap(),
    loading: isFetching || isUpdating || isDeleting,
    error: extractNestJsErrorMessage(error)
  };
};
